import { Capacitor } from "@capacitor/core";
import type { AdPlatform, HouseAdCampaign } from "@yjsgames/house-ads";
import type { RewardedAdProvider, HouseAdModalRequest } from "@yjsgames/ad-ports";
import { HouseAdProvider } from "./HouseAdProvider.js";
import { RoutingRewardedAdProvider } from "@yjsgames/ad-ports";
import type { AnalyticsSink } from "@yjsgames/analytics-ports";
import { CrazyGamesRewardedProvider } from "./CrazyGamesRewardedProvider.js";

/**
 * 웹 보상형 광고 구현 선택. "house"=자사 하우스 광고(기본), "crazygames"=CrazyGames SDK.
 * env `VITE_WEB_AD_PROVIDER`로 주입.
 */
export type WebAdProvider = "house" | "crazygames";

export interface RewardedAdProviderConfig {
  readonly analytics: AnalyticsSink;
  /** 하우스 광고 모달 표시 (ModalHost.openHouseAd). */
  readonly openModal: (req: HouseAdModalRequest<HouseAdCampaign>) => void;
  /** 하우스 광고 모달 닫기 (ModalHost.closeHouseAd). */
  readonly closeModal: () => void;
  /** LevelPlay(ironSource) 대시보드 발급 appKey — 플랫폼별, 네이티브 전용(env 주입, no-hardcode). */
  readonly appKey: string;
  /** LevelPlay rewarded placement 이름 — 네이티브 전용(env 주입). */
  readonly placement: string;
  /** 클릭 URL 분기용 플랫폼 — Capacitor.getPlatform()(ios/android/web). */
  readonly platform: AdPlatform;
  /** 모바일에서 자사광고 노출 확률 (0~1). 웹은 LevelPlay 부재라 항상 100% 자사. */
  readonly mobileShare: number;
  /** non-live 빌드는 LevelPlay 테스트 광고/테스트 스위트로 초기화해 수익 집계를 피한다. */
  readonly testMode?: boolean;
  /** 치트 — true면 라우팅이 플랫폼·확률 무관 항상 자사광고 선택(인하우스 강제). */
  readonly forceHouse?: () => boolean;
  /** 웹 보상형 광고 구현 선택 — env VITE_WEB_AD_PROVIDER. 기본 "house". */
  readonly webAdProvider?: WebAdProvider;
}

/**
 * 보상형 광고 Provider 결정 (DIP 플랫폼 분기 + 확률 라우팅 팩토리).
 *
 * - **웹 (house)**: 자사 하우스 광고 100% (기존 동작).
 * - **웹 (crazygames)**: CrazyGames SDK `requestAd("rewarded")` — 첫 클릭부터 CrazyGames SDK만 쓰도록
 *   `CrazyGamesRewardedProvider`를 동기 주입.
 * - **네이티브**: `LevelPlayRewardedProvider`를 dynamic import로 주입. `mobileShare` 확률로 house,
 *   나머지는 LevelPlay.
 *
 * 게임 로직(일일상점)은 반환된 `RewardedAdProvider`(Port)에만 의존하고 구체 구현/라우팅을 모른다.
 */
export function createRewardedAdProvider(config: RewardedAdProviderConfig): RewardedAdProvider {
  const isNative = Capacitor.isNativePlatform();
  const isCrazyGamesWeb = !isNative && config.webAdProvider === "crazygames";
  const house = new HouseAdProvider(
    config.analytics,
    config.openModal,
    config.closeModal,
    undefined,
    undefined,
    config.platform,
  );
  const routing = new RoutingRewardedAdProvider(
    house,
    isCrazyGamesWeb ? 0 : config.mobileShare,
    Math.random,
    isCrazyGamesWeb ? () => false : config.forceHouse ?? (() => false),
  );

  if (isNative) {
    void import("./LevelPlayRewardedProvider.js").then(({ LevelPlayRewardedProvider }) => {
      routing.setNativeProvider(
        new LevelPlayRewardedProvider(
          config.appKey,
          config.placement,
          config.analytics,
          config.testMode === true,
        ),
      );
    });
  } else if (isCrazyGamesWeb) {
    // CrazyGames 웹 — 첫 광고 클릭부터 SDK만 사용해야 하므로 동기 주입한다.
    routing.setNativeProvider(new CrazyGamesRewardedProvider(config.analytics));
  }

  return routing;
}
